import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { UserPlus, Mail, Lock, User, AtSign } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { useUserAuth } from "@/lib/user-auth";
import { useTheme, type LanguageCode } from "@/lib/theme-context";
import { detectHeadLang, pickT } from "@/lib/i18n";

export const Route = createFileRoute("/signup")({
  head: ({ match }) => {
    const lang = detectHeadLang(match.search as Record<string, unknown>);
    return {
      meta: [
        { title: `${pickT(lang, "إنشاء حساب", "Create account", "Créer un compte")} — Rawan UI` },
        { name: "description", content: pickT(lang, "أنشئ حسابك لحفظ المفضلة وقائمة المشاهدة وسجلك.", "Create an account to save favorites, watchlist, and history.", "Créez un compte pour enregistrer vos favoris, votre liste et votre historique.") },
        { name: "robots", content: "noindex, nofollow" },
      ],
    };
  },
  component: SignupPage,
});

function SignupPage() {
  const { language } = useTheme();
  const t = (ar: string, en: string, fr?: string) => pickT(language, ar, en, fr);
  const { user, signUp, resendVerification } = useUserAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  useEffect(() => { if (user) navigate({ to: "/account" }); }, [user, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true); setError(null);
    const res = await signUp({ email, password, displayName, username });
    setLoading(false);
    if (!res.ok) setError(localizeAuthMessage(res.error, language));
    else setSentTo(email.trim().toLowerCase());
  }

  async function onResend() {
    setResendMsg(null);
    const r = await resendVerification();
    setResendMsg(r.ok ? t("تم إرسال الرسالة مجدداً ✓", "Message sent again ✓", "Message renvoyé ✓") : localizeAuthMessage(r.error, language));
  }

  if (sentTo) {
    return (
      <div className="min-h-dvh flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-12 max-w-md grid place-items-center">
          <div className="w-full bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-card text-center animate-scale-in">
            <div className="grid place-items-center w-14 h-14 mx-auto rounded-2xl mb-4" style={{ background: "var(--gradient-primary)" }}>
              <Mail className="h-7 w-7 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-extrabold">{t("تم إنشاء حسابك بنجاح ✓", "Account created successfully ✓", "Compte créé avec succès ✓")}</h1>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              {t("أرسلنا رسالة تفعيل إلى", "We sent a verification email to", "Nous avons envoyé un e-mail d’activation à")} <strong className="text-foreground">{sentTo}</strong>.
              <br />
              {t("افتح بريدك الإلكتروني واضغط على رابط التفعيل، ثم عُد إلى تسجيل الدخول.", "Open your email, click the verification link, then return to sign in.", "Ouvrez votre e-mail, cliquez sur le lien d’activation, puis revenez à la connexion.")}
            </p>
            <div className="mt-4 text-xs text-muted-foreground bg-accent/50 border border-border rounded-lg p-3 text-start leading-relaxed">
              <strong className="text-foreground">{t("لم تجد الرسالة؟", "Can’t find the message?", "Vous ne trouvez pas le message ?")}</strong>
              <br />
              {t("تحقّق من مجلد", "Check the", "Vérifiez le dossier")} <strong className="text-foreground">{t("السبام / Spam", "Spam", "Spam")}</strong> {t("أو", "or", "ou")}
              <strong className="text-foreground"> {t("العروض الترويجية / Promotions", "Promotions", "Promotions")}</strong>،
              {t("فقد تصل الرسالة هناك أحياناً.", "the email may arrive there sometimes.", "l’e-mail peut parfois y arriver.")}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {t("لن تعمل التعليقات أو لوحة الحساب قبل تأكيد البريد.", "Comments and account dashboard will not work before email verification.", "Les commentaires et le compte ne fonctionneront qu’après confirmation de l’e-mail.")}
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <Link to="/login" className="btn-primary inline-flex items-center justify-center">
                {t("الذهاب إلى تسجيل الدخول", "Go to sign in", "Aller à la connexion")}
              </Link>
              <button
                type="button"
                onClick={onResend}
                className="text-xs text-primary hover:underline"
              >
                {t("إعادة إرسال رسالة التأكيد", "Resend verification email", "Renvoyer l’e-mail de confirmation")}
              </button>
              {resendMsg && <div className="text-xs text-muted-foreground">{resendMsg}</div>}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-12 max-w-md grid place-items-center">
        <form onSubmit={onSubmit} className="w-full bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-card animate-scale-in">
          <div className="grid place-items-center w-14 h-14 mx-auto rounded-2xl mb-4" style={{ background: "var(--gradient-primary)" }}>
            <UserPlus className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-extrabold text-center">{t("إنشاء حساب جديد", "Create a new account", "Créer un nouveau compte")}</h1>
          <p className="text-sm text-muted-foreground text-center mt-1">{t("احفظ مفضلتك وقائمة مشاهدتك عبر الأجهزة لاحقاً", "Save favorites and your watchlist across devices later", "Enregistrez vos favoris et votre liste sur vos appareils")}</p>

          <Field label={t("الاسم المعروض", "Display name", "Nom affiché")} icon={<User className="h-4 w-4 text-muted-foreground" />} value={displayName} onChange={setDisplayName} placeholder="جمال" />
          <Field label={t("اسم المستخدم", "Username", "Nom d’utilisateur")} icon={<AtSign className="h-4 w-4 text-muted-foreground" />} value={username} onChange={setUsername} placeholder="jamal" />
          <Field label={t("البريد الإلكتروني", "Email address", "Adresse e-mail")} type="email" icon={<Mail className="h-4 w-4 text-muted-foreground" />} value={email} onChange={setEmail} placeholder="you@example.com" required />
          <Field label={t("كلمة المرور", "Password", "Mot de passe")} type="password" icon={<Lock className="h-4 w-4 text-muted-foreground" />} value={password} onChange={setPassword} placeholder={t("6 خانات على الأقل", "At least 6 characters", "Au moins 6 caractères")} required />

          {error && <div className="mt-4 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg p-3">{error}</div>}

          <button type="submit" disabled={loading} className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? t("جارٍ الإنشاء…", "Creating…", "Création…") : t("إنشاء الحساب", "Create account", "Créer le compte")}
          </button>

          <div className="mt-4 text-center text-xs text-muted-foreground">
            {t("لديك حساب؟", "Already have an account?", "Vous avez déjà un compte ?")} <Link to="/login" className="text-primary hover:underline">{t("دخول", "Sign in", "Connexion")}</Link>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground text-center leading-relaxed">
            {t("سترسَل رسالة تأكيد إلى بريدك. لن يتم تفعيل الحساب قبل الضغط على الرابط.", "A verification email will be sent. Your account activates after clicking the link.", "Un e-mail de confirmation sera envoyé. Le compte sera activé après avoir cliqué sur le lien.")}
          </p>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function Field({
  label, value, onChange, type = "text", placeholder, required, icon,
}: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; required?: boolean; icon?: React.ReactNode;
}) {
  return (
    <>
      <label className="block mt-4 text-sm font-bold">{label}</label>
      <div className="relative mt-1.5">
        {icon && <span className="absolute top-1/2 -translate-y-1/2 start-3">{icon}</span>}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className={`w-full ${icon ? "ps-9" : "ps-3"} pe-3 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary`}
        />
      </div>
    </>
  );
}

function localizeAuthMessage(message: string, lang: LanguageCode): string {
  const map: Record<string, [string, string, string]> = {
    "Firebase غير متاح": ["Firebase غير متاح", "Firebase is unavailable", "Firebase est indisponible"],
    "هذا البريد مسجل مسبقاً": ["هذا البريد مسجل مسبقاً", "This email is already registered", "Cet e-mail est déjà inscrit"],
    "بريد إلكتروني غير صالح": ["بريد إلكتروني غير صالح", "Invalid email address", "Adresse e-mail invalide"],
    "كلمة المرور ضعيفة (6 خانات على الأقل)": ["كلمة المرور ضعيفة (6 خانات على الأقل)", "Weak password (at least 6 characters)", "Mot de passe faible (au moins 6 caractères)"],
    "محاولات كثيرة، حاول لاحقاً": ["محاولات كثيرة، حاول لاحقاً", "Too many attempts, try later", "Trop de tentatives, réessayez plus tard"],
    "تعذّر الاتصال بالشبكة": ["تعذّر الاتصال بالشبكة", "Network connection failed", "Échec de connexion au réseau"],
    "أعد محاولة تسجيل الدخول لإرسال الرسالة": ["أعد محاولة تسجيل الدخول لإرسال الرسالة", "Sign in again to send the email", "Reconnectez-vous pour envoyer l’e-mail"],
    "تعذّر إرسال الرسالة، حاول لاحقاً": ["تعذّر إرسال الرسالة، حاول لاحقاً", "Could not send the message, try later", "Impossible d’envoyer le message, réessayez plus tard"],
  };
  const hit = map[message];
  return hit ? pickT(lang, hit[0], hit[1], hit[2]) : message;
}
